import type { Event } from '@hedepy/common/schemas/modules/event/event'
import Image from 'next/image'
import { useState } from 'react'

import { SubscriptionStatus } from '@/legacy-src/constants/appEnums'
import useEventActions from '@/legacy-src/hooks/useEventActions/useEventActions'
import useUser from '@/legacy-src/hooks/useUser'
import { getTranslate } from '@/legacy-src/utilities/i18n'
import Toaster from '@/legacy-src/utilities/toaster'

import { Button } from '@/src/domains/design-system/components/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  type DialogProps,
} from '@/src/domains/design-system/components/Dialog'
import { useCancellationWindowHours } from '@/src/domains/events/hooks/useCancellationWindowHours'

const cancelSessionContent = {
  tooLate: {
    string: 'tooLate',
    image: 'tooLate',
    continueText: 'component.cancelSession.cancelNoRefund',
  },
  refund: {
    string: 'refund',
    image: 'refund',
    continueText: 'component.cancelSession.cancelWithRefund',
  },
  voucher: {
    string: 'voucher',
    image: 'cancel',
    continueText: 'component.button.cancelSession',
  },
  subscription: {
    string: 'subscription',
    image: 'cancel',
    continueText: 'component.button.cancelSession',
  },
  subscriptionVoucher: {
    string: 'subscriptionVoucher',
    image: 'cancel',
    continueText: 'component.button.cancelSession',
  },
  company: {
    string: 'company',
    image: 'cancel',
    continueText: 'component.button.cancelSession',
  },
}
type Props = DialogProps & {
  event: Event
}
const CancelSessionDialog: React.FC<Props> = ({ event, ...props }) => {
  const { t } = getTranslate()
  const { user } = useUser()
  const { onCancelEvent } = useEventActions()
  const [isCancelling, setIsCancelling] = useState(false)
  const { cancellationWindowHours, getIsWithinCancellationWindow } = useCancellationWindowHours()

  const getCancelCase = () => {
    const paidBySubscription = event.userSubscriptionId
    const paidByVoucher = event.voucherId
    const paidByCompany = event.companyEventQuotaId
    const isWithinCancellationWindow = getIsWithinCancellationWindow(event.startDate)

    const isSubscriptionActive = user?.userSubscriptions?.some(
      (subscription) =>
        subscription.id === event.userSubscriptionId && subscription.status === SubscriptionStatus.ACTIVE,
    )

    if (isWithinCancellationWindow) {
      return 'tooLate'
    }
    if (paidByVoucher) {
      return 'voucher'
    }
    if (paidByCompany) {
      return 'company'
    }
    if (paidBySubscription) {
      if (isSubscriptionActive) {
        return 'subscription'
      } else return 'subscriptionVoucher'
    }
    return 'refund'
  }

  const cancelCase = getCancelCase()

  const handleCancelEvent = async () => {
    try {
      setIsCancelling(true)
      await onCancelEvent({
        eventId: event.id,
        shouldRedirect: false,
        therapistId: event.therapistId,
      })
      props.onOpenChange?.(false)
    } catch {
      Toaster.error('errors.defaultError')
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <Dialog {...props}>
      <DialogContent>
        <DialogHeader className="text-center">
          <Image
            src={`/images/multibooking/${cancelSessionContent[cancelCase].image}.png`}
            alt={t(`component.cancelSession.${cancelSessionContent[cancelCase].string}.title`)}
            sizes="148px"
            className="mx-auto w-[74px]"
            width={148}
            height={128}
          />
          <DialogTitle>{t(`component.cancelSession.${cancelSessionContent[cancelCase].string}.title`)}</DialogTitle>
          <DialogDescription>
            {cancelCase === 'tooLate'
              ? t('component.cancelSession.tooLate.windowNote', { hours: cancellationWindowHours })
              : t(`component.cancelSession.${cancelSessionContent[cancelCase].string}.note`)}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-10 flex flex-col items-center gap-3 text-lg tablet:mt-5 tablet:flex-row">
          <Button size="medium" className="w-full tablet:w-1/2" disabled={isCancelling} onClick={handleCancelEvent}>
            {t(cancelSessionContent[cancelCase].continueText)}
          </Button>
          <DialogClose className="w-full border-none bg-transparent p-0 tablet:w-1/2">
            <Button size="medium" variant="secondary" className="w-full">
              {t('component.button.back')}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CancelSessionDialog
